import { api, apiSchemas } from '@verifime/api-definition';
import { TApiScanIdentityDocumentRequest, TInitialFile } from '@verifime/components';
import {
  DOCUMENT_TYPES,
  DocumentType,
  stringUtils,
  TDocument,
  TIdentityDocumentType,
} from '@verifime/utils';
import { z } from 'zod';
import { TDocumentTypeChoice, TIdentityDocumentsUploadConfig } from './types';
import {
  TApiPutIdentityDocumentRequest,
  TApiPutIdentityDocumentTypeSelectionsRequest,
  TOcrData,
} from './types';

const DEFAULT_MAX_RETRY = 30;
const DEFAULT_POLLING_INTERVAL_IN_MS = 1000;

const uploadFileByPresignUrl = (
  presignUploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Add progress event listener
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress?.(percentComplete);
      }
    });

    // Add load event listener
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        console.error(`Upload failed with status ${xhr.status}`);
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Add error event listener
    xhr.addEventListener('error', (error) => {
      console.error('Upload error:', error);
      reject(new Error('Upload failed'));
    });

    // Open and send the request
    xhr.open('PUT', presignUploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

export const entityDocumentApi = {
  upload: async (
    files: File[],
    onProgress: (progress: number, fileId: string) => void,
    entityId: string,
    documentType: TIdentityDocumentType,
  ): Promise<{
    uploadedFiles: { url: string; id?: string }[];
    additionalDetails: Record<string, any>;
  }> => {
    try {
      const fileNames = files.map((file) => file.name);
      const presingUrlResponse = await api.postV2personIdidentityDocumentTypepresign(fileNames, {
        params: {
          id: entityId,
          type: documentType,
        },
      });

      const scanFilesId = presingUrlResponse.scanFilesId as string;
      const presignUrls = presingUrlResponse.presignUrlList as z.infer<
        typeof apiSchemas.PresignUrl
      >[];
      if (!presignUrls?.length) {
        console.error('No presign upload urls found');
        throw new Error('Upload failed');
      }

      const uploadedFiles = await Promise.all(
        files.map(async (file, index) => {
          const presignUrl = presignUrls[index];
          if (!presignUrl?.url) {
            throw new Error('Invalid presign URL');
          }

          // Use a wrapper function to adapt the progress callback
          const progressWrapper = (progress: number) => {
            onProgress(progress, presignUrl.fileId);
          };

          await uploadFileByPresignUrl(presignUrl.url, file, progressWrapper);
          const fileUrl = URL.createObjectURL(file);
          return {
            url: fileUrl,
            id: presignUrl.fileId,
          };
        }),
      );

      return {
        uploadedFiles,
        additionalDetails: { scanFilesId },
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error('Upload failed');
    }
  },

  delete: async (
    fileId: string,
    entityId: string,
    documentType: TIdentityDocumentType,
  ): Promise<any> => {
    try {
      return await api.deleteV2personIdidentityDocumentTypefileFileId(undefined, {
        params: {
          fileId,
          id: entityId,
          type: documentType,
        },
      });
    } catch (error) {
      console.error('Delete error:', error);
      throw new Error('Delete failed');
    }
  },
  fetchInitialFiles: async ({
    entityId,
    documentType,
  }: {
    entityId: string;
    documentType: TIdentityDocumentType;
  }): Promise<{ uploadedFiles: TInitialFile[]; additionalDetails: Record<string, any> }> => {
    try {
      // Get list of files info first
      const { scanFilesId, itemList: listFiles } = await api.getV2personIdidentityDocumentTypefile({
        params: {
          id: entityId,
          type: documentType,
        },
      });

      if (listFiles?.length < 1) {
        return {
          uploadedFiles: [],
          additionalDetails: { scanFilesId },
        };
      }

      // Get all presign download
      const presignUrls = await Promise.all(
        listFiles.map((item) =>
          api.getV1fileEntityIdDocTypeFileIdpresignDownload({
            params: { entityId, docType: documentType, fileId: item.fileId },
          }),
        ),
      );

      return {
        uploadedFiles: presignUrls.map(({ url, fileId, filename }) => ({
          url,
          id: fileId,
          name: filename,
        })),
        additionalDetails: { scanFilesId },
      };
    } catch (error) {
      console.error('Error fetching initial file:', error);
      throw new Error('Fetching initial file failed');
    }
  },
  putIdentityDocumentTypeSelections: ({
    selections,
    customerId,
  }: TApiPutIdentityDocumentTypeSelectionsRequest) =>
    api.putV2personIdidentityDocumentselection(selections, {
      params: { id: customerId },
    }),
  putIdentityDocument: ({
    customerId,
    documentType,
    documentData,
  }: TApiPutIdentityDocumentRequest) => {
    const documentFieldName = getLowerCaseFirstLetterDocumentType(documentType);
    return api.putV2personIdidentityDocumentType(
      { [documentFieldName]: documentData },
      {
        params: {
          id: customerId,
          type: documentType,
        },
      },
    );
  },
  scanIdentityDocument: ({
    customerId,
    documentType,
    scanFilesId,
  }: TApiScanIdentityDocumentRequest) => {
    return api.getV2personIdidentityDocumentTypescanResultIdentityDocumentScanId({
      params: {
        id: customerId,
        type: documentType,
        identityDocumentScanId: scanFilesId,
      },
    });
  },
};

export function getDocumentTypesExcludeSelectedOnes({
  selectedDocumentTypes,
  documentTypeChoices,
}: {
  selectedDocumentTypes?: DocumentType[];
  documentTypeChoices?: TIdentityDocumentsUploadConfig['documentTypeChoices'];
}): TDocument[] {
  if (!documentTypeChoices || documentTypeChoices.length === 0) {
    throw new Error('Document type choices must be provided');
  }

  // Convert document type choices to TDocument array
  let documentTypes = documentTypeChoices.map(({ type: { code, label } }) => ({
    code,
    label,
  }));

  // If we have an array of document types to exclude, filter them all out
  if (selectedDocumentTypes && selectedDocumentTypes.length > 0) {
    return documentTypes.filter(
      (item) => !selectedDocumentTypes.includes(item.code as DocumentType),
    );
  }
  return documentTypes;
}

export function buildOcrData(
  documentType: TIdentityDocumentType,
  formData: Record<string, any>,
): TOcrData {
  if (!formData) {
    return null;
  }
  const lowerCaseFirstLetterDocumentType = getLowerCaseFirstLetterDocumentType(documentType);
  return {
    type: documentType,
    [lowerCaseFirstLetterDocumentType]: formData[lowerCaseFirstLetterDocumentType] || formData,
  };
}

export function getLowerCaseFirstLetterDocumentType(
  documentType: TIdentityDocumentType,
):
  | 'passport'
  | 'immiCard'
  | 'driverLicence'
  | 'photoCard'
  | 'medicareCard'
  | 'otherIdentityDocument' {
  return stringUtils.lowerCaseFirstLetter(documentType) as
    | 'passport'
    | 'immiCard'
    | 'driverLicence'
    | 'photoCard'
    | 'medicareCard'
    | 'otherIdentityDocument';
}

async function getIdentityDocumentsUploadConfig({
  maxRetry = DEFAULT_MAX_RETRY,
  pollingIntervalInMs = DEFAULT_POLLING_INTERVAL_IN_MS,
}: {
  maxRetry?: number;
  pollingIntervalInMs?: number;
}): Promise<TIdentityDocumentsUploadConfig> {
  return api.getV2personmeidentityDocumentrequirement().then((response) => {
    const { documentCount, documentTypeChoices } = response;
    return {
      documentCount,
      documentTypeChoices: documentTypeChoices.map(({ type, rules }) => {
        const result: TDocumentTypeChoice = {
          type: DOCUMENT_TYPES[type],
          rules: {
            uploadRequired: true, // default to scanRequired as before
            ...rules, // override with setting from the API
          },
        };
        return result;
      }),
      maxRetry,
      pollingIntervalInMs,
    };
  });
}

// Wrapper object to allow spyOn in tests (ES module exports are read-only)
export const identityUtils = {
  getIdentityDocumentsUploadConfig,
};
